import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { interestsList } from '../../../../common/app-config/filter-models/interests';
import { CreatorResponseDto } from '../../../../common/general-types/creators.dto';
import { SocialScreeningService } from '../../../../services/social-screening/social-screening.service';
import { ImageService } from '../../../../services/image/image.service';
import { FormatService } from '../../../../common/format/format.service';
import { listCreatorPlatformStats, CreatorPlatformStatRow } from '../../../../common/format/creator-platform-stats';
import { MagicSearchService } from '../../../creators/service/magic-search/magic-search.service';
import { CollaboratorService } from '../../../../services/collaborator/collaborator.service';
import { differenceInDays } from 'date-fns';
import { MiscService } from '../../../../services/misc/misc.service';
import { CreatorService } from '../../../../services/creator/creator.service';
import { DecimalPipe } from '@angular/common';
import { ApplicationStatusBadgeComponent } from '../../../../atoms/application-status-badge/application-status-badge.component';
import { BriefApplicationService } from '../../../../services/brief-application/brief-application.service';
import { BriefResponseDto, VideoFormatEnum } from '@/app/common/general-types/briefs.dto';
import { firstValueFrom } from 'rxjs';
import { VideoService } from '../../../../services/video/video.service';
import { VideoThumbnailComponent } from '../../../../atoms/video-thumbnail/video-thumbnail.component';
import { LdgImgComponent } from '../../../../atoms/ldg-img/ldg-img.component';
import { VideoResponseDto } from '@/app/common/general-types/videos.dto';
import { MonitoringBriefResponseDto } from '@/app/common/general-types/social-screening.dto';
import { SocialMonitoring } from '@/app/services/social-monitoring/social-monitoring';
import { HasRoleDirectiveDirective } from "@/app/common/directives/has-role/has-role-directive.directive";
import { AmbalaGroupsComponent } from '../../../creators-v2/molecules/ambala-groups/ambala-groups.component';
import { RecruitGroupService } from '../../../../services/recruit-group/recruit-group.service';

const BIO_POPOVER_MAX_WIDTH_PX = Math.round(672 * 1.3 * 1.2);

@Component({
  selector: 'cv4f-blue-gold-search-item',
  imports: [DecimalPipe, ApplicationStatusBadgeComponent, VideoThumbnailComponent, LdgImgComponent, HasRoleDirectiveDirective, AmbalaGroupsComponent],
  templateUrl: './blue-gold-search-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlueGoldSearchItemComponent implements OnInit, AfterViewInit, OnDestroy {

  VideoFormatEnum = VideoFormatEnum;
  readonly portfolioPlaceholders = [0, 1, 2] as const;

  @Input() creator!: CreatorResponseDto;
  @Input() directInvite: boolean = false;
  @Input() brief: BriefResponseDto | undefined;
  @Input() compact: boolean = false;
  @Input() monitoringBrief: MonitoringBriefResponseDto | undefined;

  @ViewChild('cardRoot') cardRoot?: ElementRef<HTMLElement>;
  @ViewChild('interestsMoreBtn') interestsMoreBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('bioPreview') bioPreview?: ElementRef<HTMLParagraphElement>;
  @ViewChild('bioMoreBtn') bioMoreBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('bioPopoverPanel') bioPopoverPanel?: ElementRef<HTMLElement>;
  @ViewChild('interestsPopoverPanel') interestsPopoverPanel?: ElementRef<HTMLElement>;

  today: Date = new Date();
  createdDaysAgo = signal<number>(0);
  hometown = signal<string>('');

  totalFollowersDisplay = '';
  creatorAge = 0;
  previewInterestLabels: string[] = [];

  /** Services  */
  public imageService = inject(ImageService);
  public formatService = inject(FormatService);
  magicSearchService = inject(MagicSearchService);
  public collaboratorsService = inject(CollaboratorService);
  public socialScreeningService = inject(SocialScreeningService);
  private miscService = inject(MiscService);
  private creatorService = inject(CreatorService);
  private briefApplicationService = inject(BriefApplicationService);
  private videoService = inject(VideoService);
  private socialMonitoringService = inject(SocialMonitoring);
  private recruitGroupService = inject(RecruitGroupService);
  /** Services - END */

  private popoverDismissCleanup: (() => void) | null = null;
  private bioPopoverDismissCleanup: (() => void) | null = null;
  private intersectionObserver?: IntersectionObserver;
  private portfolioLoadStarted = false;

  inviteSending = signal<boolean>(false);
  showAddToGroup = signal(false);
  private readonly groupMembershipTick = signal(0);
  readonly isInterestsPopoverOpen = computed(
    () => this.magicSearchService.openInterestsPopoverCreatorId() === this.creator.blazeFirebaseUid,
  );
  readonly isBioPopoverOpen = computed(
    () => this.magicSearchService.openBioPopoverCreatorId() === this.creator.blazeFirebaseUid,
  );
  interestsPopoverStyle = signal<{ top: string; left: string } | null>(null);
  bioPopoverStyle = signal<{ top: string; left: string } | null>(null);
  bioIsTruncated = signal(false);
  isInView = signal<boolean>(false);
  portfolioLoading = signal<boolean>(false);

  /** Full portfolio list for viewer navigation; preview row shows first three. */
  portfolioVideos = signal<VideoResponseDto[]>([]);

  /** Loaded async; kept in a signal so updates are not applied mid–change-detection (avoids NG0100). */
  portfolioPreviewVideos = signal<VideoResponseDto[]>([]);

  private readonly syncPopoverDismissListeners = effect(() => {
    if (!this.isInterestsPopoverOpen()) {
      this.interestsPopoverStyle.set(null);
      this.detachPopoverDismissListeners();
    }
  });

  private readonly syncBioPopoverDismissListeners = effect(() => {
    if (!this.isBioPopoverOpen()) {
      this.bioPopoverStyle.set(null);
      this.detachBioPopoverDismissListeners();
    }
  });

  ngOnInit(): void {
    if (this.creator.createdDate) {
      this.createdDaysAgo.set(differenceInDays(this.today, this.creator.createdDate));
    }
    this.hometown.set(this.miscService.getShortHometown(this.creator.homeTownName));
    this.creatorAge = this.creator.dateOfBirth
      ? this.formatService.calculateAge(this.creator.dateOfBirth)
      : 0;
    const totalFollowers =
      (this.creator.tikTokFollowers ?? 0)
      + (this.creator.instaFollowers ?? 0)
      + (this.creator.snapchatFollowers ?? 0)
      + (this.creator.youtubeSubscribers ?? 0);
    this.totalFollowersDisplay = this.formatService.convertIntToString(totalFollowers);
    this.previewInterestLabels = (this.creator.interests?.slice(0, 3) ?? [])
      .map((interest) => this.getInterestText(interest))
      .filter((label) => label.length > 0);
  }

  displayName(): string {
    const first = this.creator.firstName?.trim() ?? '';
    const lastInitial = this.creator.lastName?.trim()?.charAt(0);
    return lastInitial ? `${first} ${lastInitial}.` : first;
  }

  platformStatRows(): CreatorPlatformStatRow[] {
    return listCreatorPlatformStats(this.creator.ssCreator, {
      filters: this.magicSearchService.filtersObjectSignal(),
      followerCounts: {
        tiktok: this.creator.tikTokFollowers,
        instagram: this.creator.instaFollowers,
        youtube: this.creator.youtubeSubscribers,
        snapchat: this.creator.snapchatFollowers,
      },
    });
  }

  ngAfterViewInit(): void {
    const element = this.cardRoot?.nativeElement;
    if (!element || typeof IntersectionObserver === 'undefined') {
      this.isInView.set(true);
      this.loadPortfolioVideosIfNeeded();
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return;
        }

        this.isInView.set(true);
        this.loadPortfolioVideosIfNeeded();
        queueMicrotask(() => this.measureBioTruncation());
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = undefined;
      },
      { rootMargin: '240px 0px' },
    );

    this.intersectionObserver.observe(element);
    queueMicrotask(() => this.measureBioTruncation());
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.detachPopoverDismissListeners();
    this.detachBioPopoverDismissListeners();
  }

  hasRealBio(): boolean {
    return (this.creator.bio?.trim().length ?? 0) > 0;
  }

  displayBio(): string {
    const bio = this.creator.bio?.trim();
    return bio && bio.length > 0 ? bio : 'Bio coming soon...';
  }

  private measureBioTruncation(): void {
    const element = this.bioPreview?.nativeElement;
    if (!element) {
      return;
    }

    this.bioIsTruncated.set(element.scrollHeight > element.clientHeight + 1);
  }

  /** The creator's status on the brief Find Creators was opened for, if they already have an application. */
  existingApplicationStatus(): number | undefined {
    return this.collaboratorsService.briefApplicationStatuses().get(this.creator.blazeFirebaseUid);
  }

  isCreatorSelected(): boolean {
    return this.collaboratorsService.selectedCreators()
      .some((selected) => selected.blazeFirebaseUid === this.creator.blazeFirebaseUid);
  }

  onCollaborate(event: Event): void {
    const wasSelected = this.isCreatorSelected();
    this.collaboratorsService.selectCreator(this.creator);

    if (!wasSelected && this.isCreatorSelected()) {
      const button = event.currentTarget;
      if (button instanceof HTMLElement) {
        this.collaboratorsService.triggerSelectionGuide(button.getBoundingClientRect());
      }
    }
  }

  openAddToGroup(): void {
    this.closeInterestsPopover();
    this.closeBioPopover();
    this.showAddToGroup.set(true);
  }

  closeAddToGroup(): void {
    this.showAddToGroup.set(false);
    this.groupMembershipTick.update((count) => count + 1);
  }

  groupMembershipCount(): number {
    this.groupMembershipTick();
    this.recruitGroupService.creatorGroupMembershipRevision();
    return this.recruitGroupService.membershipCountInVisibleGroups(this.creator.recruitGroupIds);
  }

  isInAnyGroup(): boolean {
    return this.groupMembershipCount() > 0;
  }

  inGroupBadgeLabel(): string {
    const count = this.groupMembershipCount();
    return count === 1 ? 'In a group' : `In ${count} groups`;
  }

  hasPortfolioThumbnail(video: VideoResponseDto): boolean {
    return !!video.thumbnailURL && !video.thumbnailURL.includes('www.tikwm.com');
  }

  private loadPortfolioVideosIfNeeded(): void {
    if (this.compact || this.portfolioLoadStarted) {
      return;
    }

    this.portfolioLoadStarted = true;
    this.portfolioLoading.set(true);
    void this.getCreatorPortfolioVideos();
  }

  toggleInterestsPopover(event: Event): void {
    event.stopPropagation();

    if (this.isInterestsPopoverOpen()) {
      this.closeInterestsPopover();
      return;
    }

    this.magicSearchService.openBioPopoverCreatorId.set(null);
    this.magicSearchService.openInterestsPopoverCreatorId.set(this.creator.blazeFirebaseUid);
    this.updateInterestsPopoverPosition();
    queueMicrotask(() => {
      this.updateInterestsPopoverPosition();
      this.attachPopoverDismissListeners();
    });
  }

  private updateInterestsPopoverPosition(): void {
    const button = this.interestsMoreBtn?.nativeElement;
    if (!button) {
      return;
    }

    const rect = button.getBoundingClientRect();
    const popoverWidth = Math.min(384, window.innerWidth - 16);
    const maxLeft = window.innerWidth - popoverWidth - 8;
    const left = Math.max(8, Math.min(rect.left, maxLeft));

    this.interestsPopoverStyle.set({
      top: `${rect.bottom + 6}px`,
      left: `${left}px`,
    });
  }

  onInterestsPopoverPanelClick(event: Event): void {
    event.stopPropagation();
  }

  closeInterestsPopover(): void {
    if (!this.isInterestsPopoverOpen()) {
      return;
    }

    this.magicSearchService.openInterestsPopoverCreatorId.set(null);
    this.interestsPopoverStyle.set(null);
    this.detachPopoverDismissListeners();
  }

  private attachPopoverDismissListeners(): void {
    this.detachPopoverDismissListeners();

    const onDocumentClick = () => this.closeInterestsPopover();
    const onDocumentKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.closeInterestsPopover();
      }
    };
    const onScroll = (event: Event) => {
      if (this.isScrollInsidePopover(event, this.interestsPopoverPanel)) {
        return;
      }
      this.closeInterestsPopover();
    };

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);
    window.addEventListener('scroll', onScroll, true);

    this.popoverDismissCleanup = () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onDocumentKeydown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }

  private detachPopoverDismissListeners(): void {
    this.popoverDismissCleanup?.();
    this.popoverDismissCleanup = null;
  }

  toggleBioPopover(event: Event): void {
    event.stopPropagation();

    if (this.isBioPopoverOpen()) {
      this.closeBioPopover();
      return;
    }

    this.magicSearchService.openInterestsPopoverCreatorId.set(null);
    this.magicSearchService.openBioPopoverCreatorId.set(this.creator.blazeFirebaseUid);
    this.updateBioPopoverPosition();
    queueMicrotask(() => {
      this.updateBioPopoverPosition();
      this.attachBioPopoverDismissListeners();
    });
  }

  private updateBioPopoverPosition(): void {
    const button = this.bioMoreBtn?.nativeElement;
    if (!button) {
      return;
    }

    const rect = button.getBoundingClientRect();
    const popoverWidth = Math.min(BIO_POPOVER_MAX_WIDTH_PX, window.innerWidth - 16);
    const maxLeft = window.innerWidth - popoverWidth - 8;
    const left = Math.max(8, Math.min(rect.left, maxLeft));

    this.bioPopoverStyle.set({
      top: `${rect.bottom + 6}px`,
      left: `${left}px`,
    });
  }

  onBioPopoverPanelClick(event: Event): void {
    event.stopPropagation();
  }

  closeBioPopover(): void {
    if (!this.isBioPopoverOpen()) {
      return;
    }

    this.magicSearchService.openBioPopoverCreatorId.set(null);
    this.bioPopoverStyle.set(null);
    this.detachBioPopoverDismissListeners();
  }

  private attachBioPopoverDismissListeners(): void {
    this.detachBioPopoverDismissListeners();

    const onDocumentClick = () => this.closeBioPopover();
    const onDocumentKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.closeBioPopover();
      }
    };
    const onScroll = (event: Event) => {
      if (this.isScrollInsidePopover(event, this.bioPopoverPanel)) {
        return;
      }
      this.closeBioPopover();
    };

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);
    window.addEventListener('scroll', onScroll, true);

    this.bioPopoverDismissCleanup = () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onDocumentKeydown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }

  private detachBioPopoverDismissListeners(): void {
    this.bioPopoverDismissCleanup?.();
    this.bioPopoverDismissCleanup = null;
  }

  private isScrollInsidePopover(event: Event, panel?: ElementRef<HTMLElement>): boolean {
    const target = event.target;
    const panelElement = panel?.nativeElement;
    return !!(panelElement && target instanceof Node && panelElement.contains(target));
  }

  getInterestText(interestId: string | number): string {
    const id = String(interestId);

    if (!/^\d+$/.test(id)) {
      return id;
    }

    const fromApi = this.creatorService.shoutCreatorInterests?.find(
      (interest) => String(interest.id) === id,
    );
    if (fromApi?.tagName) {
      return fromApi.tagName;
    }

    return interestsList.find((interest) => String(interest.id) === id)?.name ?? '';
  }

  getAllInterestLabels(): string[] {
    if (!this.creator.interests?.length) {
      return [];
    }

    return this.creator.interests
      .map((interest) => this.getInterestText(interest))
      .filter((label) => label.length > 0);
  }

  async getCreatorPortfolioVideos() {
    try {
      const portfolioVideos = await firstValueFrom(
        this.videoService.getCreatorPortfolioVideos(this.creator.blazeFirebaseUid),
      );
      const preview = portfolioVideos.slice(0, 3);
      queueMicrotask(() => {
        this.portfolioVideos.set(portfolioVideos);
        this.creator.videos = preview;
        this.portfolioPreviewVideos.set(preview);
        this.portfolioLoading.set(false);
      });
    } catch (err) {
      console.warn('Error getting creator portfolio videos: ', err);
      this.portfolioLoading.set(false);
    }
  }

  inviteNow() {
    console.log('inviteNow(): ', this.creator.blazeFirebaseUid);
    if (this.brief) {
      this.inviteSending.set(true);
      this.briefApplicationService.createBriefApplication(this.creator, this.brief).subscribe({
        next: (response) => {
          console.log('response: ', response);
          this.creator.briefApplication = response;
          this.inviteSending.set(false);
        },
        error: (error) => {
          console.log('error: ', error);
          this.inviteSending.set(false);
        }
      });
    }
  }

  addToMonitoring() {
    console.log('addToMonitoring(): ', this.creator.blazeFirebaseUid);
    if (this.monitoringBrief) {
      this.inviteSending.set(true);
      this.socialMonitoringService.createInquiry(this.creator.blazeFirebaseUid, this.monitoringBrief.id).subscribe({
        next: (response) => {
          console.log('response: ', response);
          this.creator.socialMonitoringInquiry = response;
          this.inviteSending.set(false);
        },
        error: (error) => {
          console.log('error: ', error);
          this.inviteSending.set(false);
        }
      });
    }
  }

  launchPortfolio() {
    const results = this.magicSearchService.blueSearchResults() ?? [];
    const navItems = results.map((creator) => ({ creator }));
    this.magicSearchService.openCreatorPortfolio(this.creator, { navItems });
  }

  openSocialProfile(platform: 'tiktok' | 'instagram' | 'youtube' | 'snapchat'): void {
    const results = this.magicSearchService.blueSearchResults() ?? [];
    const navItems = results.map((creator) => ({ creator }));
    this.magicSearchService.openCreatorPortfolio(this.creator, {
      navItems,
      socialTab: platform,
    });
  }
  
  previewVideo(video: VideoResponseDto) {
    const playlist = this.portfolioVideos();
    this.magicSearchService.previewVideoPlaylist.set(playlist.length > 0 ? playlist : [video]);
    this.magicSearchService.previewVideo.set(video);
  }
}
